/**
 * WebSocket Event Types
 *
 * These are lightweight notification events. The WebSocket connection sends
 * notifications that something changed, and the frontend should fetch fresh
 * data via REST API.
 *
 * Pattern: Event Notification + REST Fetch
 * - WebSocket notifies: "Device X status changed"
 * - Frontend fetches: GET /api/devices/X
 */
import type { DeviceType } from '@/types/deviceType';

export enum WebSocketEventType {
  // === Device Events ===
  DEVICE_STATUS_CHANGED = 'device.status.changed',
  DEVICE_CREATED = 'device.created',
  DEVICE_DELETED = 'device.deleted',
  DEVICE_UPDATED = 'device.updated',
  DEVICE_DISCOVERED = 'device.discovered',

  // === Connection/Topology Events ===
  CONNECTION_DISCOVERED = 'connection.discovered',
  CONNECTION_REMOVED = 'connection.removed',
  CONNECTION_UPDATED = 'connection.updated',
  TOPOLOGY_CHANGED = 'topology.changed', // Bulk topology change

  // === Interface Events ===
  INTERFACE_STATUS_CHANGED = 'interface.status.changed',
  INTERFACE_UPDATED = 'interface.updated',

  // === Gateway Events ===
  GATEWAY_ONLINE = 'gateway.online',
  GATEWAY_OFFLINE = 'gateway.offline',
  GATEWAY_STATUS_CHANGED = 'gateway.status.changed',
  // Software deployment/convergence status: 'converging' | 'applied' | 'failed'.
  GATEWAY_SOFTWARE_STATUS_CHANGED = 'gateway.software.status.changed',
  // Live install progress during an update. Deliberately separate from the
  // status event above, which also invalidates the whole gateway list —
  // reusing it would refetch that list every few seconds for the length of
  // every install.
  GATEWAY_SOFTWARE_PROGRESS = 'gateway.software.progress',
  GATEWAY_UPDATED = 'gateway.updated',
  GATEWAY_DELETED = 'gateway.deleted',
  GATEWAY_DETECTED = 'gatewayDetected', // Detected via claim code beaconing

  // === Credential Events ===
  CREDENTIAL_CREATED = 'credential.created',
  CREDENTIAL_DELETED = 'credential.deleted',

  // === Alert & Incident Events ===
  ALERT_TRIGGERED = 'alert.triggered',
  ALERT_RESOLVED = 'alert.resolved',
  INCIDENT_CREATED = 'incident.created',
  INCIDENT_UPDATED = 'incident.updated',

  // === Change Management Events ===
  CM_CREATED = 'cm.created',
  CM_STATUS_CHANGED = 'cm.status.changed',
  CM_APPROVED = 'cm.approved',
  CM_REJECTED = 'cm.rejected',
  CM_COMPLETED = 'cm.completed',

  // === Network Plan Events ===
  PLAN_EXECUTION_STARTED = 'plan.execution.started',
  PLAN_STEP_STARTED = 'plan.step.started',
  PLAN_STEP_COMPLETED = 'plan.step.completed',
  PLAN_STEP_FAILED = 'plan.step.failed',
  PLAN_STEP_SKIPPED = 'plan.step.skipped',
  PLAN_COMPLETED = 'plan.completed',
  PLAN_FAILED = 'plan.failed',
  PLAN_CANCELLED = 'plan.cancelled',

  // === User/Organization Events ===
  USER_INVITED = 'user.invited',
  USER_REMOVED = 'user.removed',
  PERMISSION_CHANGED = 'permission.changed',
  DIVISION_CREATED = 'division.created',
  DIVISION_DELETED = 'division.deleted',
  REGION_CREATED = 'region.created',
  REGION_DELETED = 'region.deleted',

  // === Notification Events ===
  NOTIFICATION_NEW = 'notification.new',
  NOTIFICATION_CREATED = 'notification.created',

  // === System Events ===
  PING = 'ping',
  PONG = 'pong',
  SUBSCRIPTION_CONFIRMED = 'subscription.confirmed',
  ERROR = 'error',

  // === Banner Events ===
  BANNER_UPDATE = 'banner.update',

  // ===================================================================
  // Real-time coverage expansion — events the backend now emits on every
  // entity change path. Names match the backend `eventType` strings exactly.
  // ===================================================================

  // --- Device sub-resources (gateway-reported, device-scoped) ---
  WIRELESS_CLIENT_CHANGED = 'wireless.client.changed',
  DEVICE_VLANS_CHANGED = 'device.vlans.changed',
  DEVICE_ROUTES_CHANGED = 'device.routes.changed',
  DEVICE_STP_CHANGED = 'device.stp.changed',
  DEVICE_STP_INCONSISTENCY_CHANGED = 'device.stp.inconsistency.changed',
  DEVICE_COLLECTOR_HEALTH_CHANGED = 'device.collector_health.changed',
  DEVICE_CONNECTION_HEALTH_CHANGED = 'device.connection_health.changed',
  DEVICE_SECURITY_POLICY_UPDATED = 'device.security_policy.updated',
  DEVICE_SECURITY_POLICY_ALLOWED_CHANGED = 'device.security_policy.allowed_changed',
  DEVICE_CONFIG_SNAPSHOT_CREATED = 'device.config_snapshot.created',
  DEVICE_CONFIG_RESTORE_UPDATED = 'device.config_restore.updated',
  DEVICE_OID_DUMP_CREATED = 'device.oid_dump.created',

  // --- Topology (replaces the never-emitted connection.*/topology.changed) ---
  TOPOLOGY_EDGE_CHANGED = 'topology.edge.changed',

  // --- Endpoints (client devices via FDB/ARP) ---
  ENDPOINT_CREATED = 'endpoint.created',
  ENDPOINT_UPDATED = 'endpoint.updated',

  // --- Account / billing access state ---
  ACCOUNT_SUSPENDED = 'account.suspended',

  // --- Controller integrations (UniFi/Omada/…) ---
  CONTROLLER_INTEGRATION_CREATED = 'controller_integration.created',
  CONTROLLER_INTEGRATION_UPDATED = 'controller_integration.updated',
  CONTROLLER_INTEGRATION_DELETED = 'controller_integration.deleted',
  CONTROLLER_SYNC_HEALTH_CHANGED = 'controller.sync_health.changed',

  // --- Alert definitions & overrides ---
  ALERT_DEFINITION_CREATED = 'alert_definition.created',
  ALERT_DEFINITION_UPDATED = 'alert_definition.updated',
  ALERT_DEFINITION_DELETED = 'alert_definition.deleted',
  ALERT_DEFINITION_EXECUTED = 'alert_definition.executed',
  ALERT_OVERRIDE_CREATED = 'alert_override.created',
  ALERT_OVERRIDE_UPDATED = 'alert_override.updated',
  ALERT_OVERRIDE_DELETED = 'alert_override.deleted',

  // --- RBAC: teams, roles, invitations ---
  TEAM_CREATED = 'team.created',
  TEAM_UPDATED = 'team.updated',
  TEAM_DELETED = 'team.deleted',
  TEAM_MEMBERSHIP_CHANGED = 'team.membership_changed',
  ROLE_CREATED = 'role.created',
  ROLE_UPDATED = 'role.updated',
  ROLE_DELETED = 'role.deleted',
  ROLE_PERMISSIONS_CHANGED = 'role.permissions_changed',
  USER_INVITATION_CREATED = 'user_invitation.created',
  USER_INVITATION_UPDATED = 'user_invitation.updated',
  USER_INVITATION_DELETED = 'user_invitation.deleted',

  // --- Transports / provisioning tokens / org / security policy ---
  TRANSPORT_DEFINITION_CREATED = 'transport_definition.created',
  TRANSPORT_DEFINITION_UPDATED = 'transport_definition.updated',
  TRANSPORT_DEFINITION_DELETED = 'transport_definition.deleted',
  PROVISIONING_TOKEN_CREATED = 'provisioning_token.created',
  PROVISIONING_TOKEN_UPDATED = 'provisioning_token.updated',
  ORGANIZATION_UPDATED = 'organization.updated',
  SECURITY_POLICY_UPDATED = 'security_policy.updated',
  SECURITY_POLICY_DELETED = 'security_policy.deleted',

  // --- Network plans (CRUD, to complement the execution events above) ---
  PLAN_CREATED = 'plan.created',
  PLAN_UPDATED = 'plan.updated',
  PLAN_DELETED = 'plan.deleted',
  PLAN_ARCHIVED = 'plan.archived',
  PLAN_RETRIED = 'plan.retried',
  PLAN_STEP_CREATED = 'plan.step.created',
  PLAN_STEP_UPDATED = 'plan.step.updated',
  PLAN_STEP_DELETED = 'plan.step.deleted',
  PLAN_STEP_REORDERED = 'plan.step.reordered',
  PLAN_STEP_RETRIED = 'plan.step.retried',

  // --- Change management (full state-transition + activity coverage) ---
  CM_CREATED_AUTOMATED = 'cm.created.automated',
  CM_UPDATED = 'cm.updated',
  CM_STATE_CHANGED = 'cm.state_changed',
  CM_RETRIED = 'cm.retried',
  CM_COMMENT_ADDED = 'cm.comment.added',
  CM_PLAN_ATTACHED = 'cm.plan.attached',
  CM_PLAN_DETACHED = 'cm.plan.detached',
  CM_PLAN_REORDERED = 'cm.plan.reordered',
}

/**
 * Base WebSocket message structure
 */
export interface WebSocketMessage<T = unknown> {
  eventType: WebSocketEventType;
  timestamp: string;
  payload: T;
  organizationId: string;
  divisionId?: string;
  regionId?: string;
}

/**
 * Subscription scope for filtering events
 */
export interface SubscriptionScope {
  type: 'region' | 'division' | 'organization' | 'user' | 'device_list' | 'claim_code' | 'proxy_session' | 'realization_session';
  regionId?: string;
  divisionId?: string;
  userId?: string;
  deviceIds?: string[];
  claimCode?: string;
  sessionId?: string;
  deviceId?: string;
  // Optional filters
  deviceTypes?: string[];
  includeMetrics?: boolean;
}

/**
 * Message sent from frontend to backend to subscribe to events
 */
export interface SubscribeMessage {
  type: 'subscribe';
  scope: SubscriptionScope;
  /** The active context, for a subscription the backend authorizes in one
   *  (a terminal session). */
  contextId?: string | null;
}

/**
 * Message sent from frontend to backend to unsubscribe
 */
export interface UnsubscribeMessage {
  type: 'unsubscribe';
  scope: SubscriptionScope;
}

/**
 * Proxy data message (binary terminal I/O)
 */
export interface ProxyDataMessage {
  type: 'proxy_data';
  sessionId: string;
  data: string; // base64 encoded
}

/**
 * Proxy close message
 */
export interface ProxyCloseMessage {
  type: 'proxy_close';
  sessionId: string;
}

/**
 * Message sent from frontend to backend (generic)
 */
export type ClientMessage = SubscribeMessage | UnsubscribeMessage | PingMessage | ProxyDataMessage | ProxyCloseMessage;

export interface PingMessage {
  type: 'ping';
  timestamp: string;
}

/**
 * Raw message handler for non-standard WebSocket messages (e.g., proxy data)
 * Return true if the message was handled and should skip normal processing
 */
export type RawMessageHandler = (data: unknown) => boolean;

// === Event Payload Types ===

/**
 * Lightweight notification: Device status changed
 * Frontend should: Invalidate React Query cache and/or fetch fresh data
 */
export interface DeviceStatusChangedPayload {
  deviceId: string;
  oldStatus: string;
  newStatus: string;
  timestamp: string;
  regionId: string;
}

/**
 * Lightweight notification: Device created
 */
export interface DeviceCreatedPayload {
  deviceId: string;
  deviceType: DeviceType;
  regionId: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Device deleted
 */
export interface DeviceDeletedPayload {
  deviceId: string;
  regionId: string;
}

/**
 * Lightweight notification: Device updated
 */
export interface DeviceUpdatedPayload {
  deviceId: string;
  name?: string;
  status?: string;
  regionId: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Device discovered (via LLDP)
 */
export interface DeviceDiscoveredPayload {
  deviceId: string;
  name?: string;
  ipAddress?: string;
  status?: string;
  regionId: string;
}

/**
 * Lightweight notification: Interface updated
 */
export interface InterfaceUpdatedPayload {
  deviceId: string;
  changesProcessed: number;
  timestamp: string;
  regionId: string;
}

/**
 * Lightweight notification: Connection discovered
 */
export interface ConnectionDiscoveredPayload {
  connectionId: string;
  sourceDeviceId: string;
  targetDeviceId: string;
  connectionType: string;
  regionId: string;
}

/**
 * Lightweight notification: Connection removed
 */
export interface ConnectionRemovedPayload {
  connectionId: string;
  sourceDeviceId: string;
  targetDeviceId: string;
  regionId: string;
}

/**
 * Lightweight notification: Gateway status changed
 */
export interface GatewayStatusChangedPayload {
  gatewayId: string;
  oldStatus?: string;
  newStatus?: string;
  status?: 'online' | 'offline';
  regionId: string;
}

/**
 * A gateway's software deployment status changed (version convergence).
 * Pushed as the update progresses so the version UI reflects it live:
 *  - 'converging' — an apply is in flight (download/stage or ISO reboot)
 *  - 'applied'    — running == desired
 *  - 'failed'     — permanent local verdict or a timed-out apply
 */
export interface GatewaySoftwareStatusChangedPayload {
  gatewayId: string;
  phase: 'converging' | 'applied' | 'failed';
  version?: string;
  error?: string;
}

/**
 * Live install progress. Advisory and lossy — the authoritative convergence
 * verdict arrives on GATEWAY_SOFTWARE_STATUS_CHANGED.
 */
export interface GatewaySoftwareProgressPayload {
  gatewayId: string;
  version: string;
  /** Stable id: 'preparing' | 'downloading' | … — key off this, not `label`. */
  phase: string;
  /** Human wording authored by the gateway. */
  label?: string | null;
  detail?: string | null;
  /** 0-100, or null when the download total is unknown. Not 0. */
  percent?: number | null;
  at: string;
}

/**
 * Lightweight notification: Gateway updated
 */
export interface GatewayUpdatedPayload {
  gatewayId: string;
  name?: string;
  description?: string;
  regionId: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Gateway deleted
 */
export interface GatewayDeletedPayload {
  gatewayId: string;
  name?: string;
  regionId: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Gateway detected via claim code beaconing
 */
export interface GatewayDetectedPayload {
  claimCode: string;
  gateway: {
    hardwareId: string;
    serial: string;
    softwareVersion: string;
    localIp: string;
    detectedAt: string;
  };
}

/**
 * Lightweight notification: Credential created
 */
export interface CredentialCreatedPayload {
  credentialId: string;
  name?: string;
  type?: string;
  regionId: string;
  divisionId?: string;
  organizationId: string;
}

/**
 * Lightweight notification: Credential deleted
 */
export interface CredentialDeletedPayload {
  credentialId: string;
  name?: string;
  regionId: string;
  divisionId?: string;
  organizationId: string;
}

/**
 * Lightweight notification: Division created
 */
export interface DivisionCreatedPayload {
  divisionId: string;
  organizationId: string;
}

/**
 * Lightweight notification: Division deleted
 */
export interface DivisionDeletedPayload {
  divisionId: string;
  organizationId: string;
}

/**
 * Lightweight notification: Region created
 */
export interface RegionCreatedPayload {
  regionId: string;
  organizationId: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Region deleted
 */
export interface RegionDeletedPayload {
  regionId: string;
  organizationId: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Alert triggered
 */
export interface AlertTriggeredPayload {
  alertId: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  deviceId?: string;
  regionId?: string;
}

/**
 * Lightweight notification: Incident created
 */
export interface IncidentCreatedPayload {
  incidentId: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  deviceId?: string;
  regionId?: string;
  divisionId?: string;
}

/**
 * Lightweight notification: Incident updated
 */
export interface IncidentUpdatedPayload {
  incidentId: string;
  status?: string;
  severity?: string;
  regionId?: string;
  divisionId?: string;
}

/**
 * Lightweight notification: New general notification
 */
export interface NotificationPayload {
  notificationId: string;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  actionUrl?: string;
}

/**
 * Lightweight notification: Network plan event
 */
export interface PlanEventPayload {
  resourceId: string; // plan ID
}

/**
 * Subscription confirmation from backend
 */
export interface SubscriptionConfirmedPayload {
  scope: SubscriptionScope;
  subscribedTopics: string[];
}

/**
 * Error message from backend
 */
export interface ErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

/**
 * Lightweight notification: a device-scoped sub-resource changed (wireless,
 * VLANs, STP, collector/connection health, security policy, config snapshot,
 * OID dump). All device.* sub-resource events share this shape.
 */
export interface DeviceScopedEventPayload {
  deviceId: string;
  regionId?: string;
  divisionId?: string;
  // event-specific extras (counts, interfaceId, dumpId, …) are present but optional
  [key: string]: unknown;
}

/**
 * Lightweight notification: a topology edge (LLDP adjacency) changed.
 */
export interface TopologyEdgeChangedPayload {
  regionId?: string;
  edgesChanged?: number;
  removedEdges?: number;
}

/**
 * Lightweight notification: an endpoint (client device) appeared or changed.
 */
export interface EndpointEventPayload {
  endpointId: string;
}

/**
 * Lightweight notification: a change-management resource changed. Backend
 * sends the CM id as `resourceId`.
 */
export interface CmEventPayload {
  resourceId: string;
}

/**
 * Lightweight notification: a generic org-scoped entity changed, carrying its
 * id (controllers, alerts, teams, roles, invitations, transports, tokens…).
 */
export interface GenericEntityEventPayload {
  id?: string;
  resourceId?: string;
}

/**
 * Type guard for WebSocket messages
 */
export function isWebSocketMessage(data: unknown): data is WebSocketMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'eventType' in data &&
    'timestamp' in data &&
    'payload' in data
  );
}

/**
 * Message handler function type
 */
export type MessageHandler<T = unknown> = (message: WebSocketMessage<T>) => void;

/**
 * Event-specific message handler type
 */
export type EventHandler<T = unknown> = (payload: T, message: WebSocketMessage<T>) => void;
